import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { base44 } from "api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "../components/Layout";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "components/ui/table";
import { Plus, Calendar, Euro, Users, TrendingUp, Pencil, Trash2, X, ChevronDown, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "components/ui/alert-dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";

const PROVIDER_STYLES = {
  sumup:      { label: 'SumUp',      className: 'bg-blue-100 text-blue-700' },
  lightspeed: { label: 'Lightspeed', className: 'bg-green-100 text-green-700' },
  orderbird:  { label: 'Orderbird',  className: 'bg-orange-100 text-orange-700' },
};

const PAYMENT_LABELS = {
  card:         'paymentCard',
  cash:         'paymentCash',
  contactless:  'paymentContactless',
};

function SourceBadge({ provider, t }) {
  if (!provider) return <span className="text-xs text-slate-400">{t('sales.sourceManual')}</span>;
  const style = PROVIDER_STYLES[provider];
  if (!style) return <span className="text-xs text-slate-500">{provider}</span>;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.className}`}>
      {style.label}
    </span>
  );
}

function PaymentBadge({ paymentType, t }) {
  if (!paymentType) return <span className="text-xs text-slate-400">—</span>;
  const labelKey = PAYMENT_LABELS[paymentType.toLowerCase()];
  const label = labelKey ? t(`sales.${labelKey}`) : paymentType;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
      {label}
    </span>
  );
}

function LineItemsRow({ sale, t }) {
  const items = sale.line_items || [];
  if (items.length === 0) {
    return <p className="text-xs text-slate-400 py-2">{t('sales.noLineItems')}</p>;
  }
  return (
    <table className="w-full text-xs mt-1">
      <thead>
        <tr className="text-slate-500 border-b">
          <th className="text-left py-1 pr-3">{t('sales.lineItemsName')}</th>
          <th className="text-right py-1 pr-3">{t('sales.lineItemsQty')}</th>
          <th className="text-right py-1 pr-3">{t('sales.lineItemsUnit')}</th>
          <th className="text-right py-1 pr-3">{t('sales.lineItemsTotal')}</th>
          <th className="text-right py-1">{t('sales.lineItemsVat')}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((li) => (
          <tr key={li.id} className="border-b border-slate-50 last:border-0">
            <td className="py-1 pr-3 text-slate-700">{li.name}</td>
            <td className="py-1 pr-3 text-right text-slate-600">{parseFloat(li.quantity)}</td>
            <td className="py-1 pr-3 text-right text-slate-600">€{parseFloat(li.unit_price).toFixed(2)}</td>
            <td className="py-1 pr-3 text-right font-medium text-slate-800">€{parseFloat(li.total_price).toFixed(2)}</td>
            <td className="py-1 text-right text-slate-500">{li.tax_rate || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function Sales() {
  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [deleteSaleId, setDeleteSaleId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [newSale, setNewSale] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    guests_count: '',
    total_amount: '',
    shift: 'Abend',
    items_sold: []
  });

  const queryClient = useQueryClient();

  const { data: sales, isLoading } = useQuery({
    queryKey: ['sales'],
    queryFn: () => base44.entities.Sale.list('-date'),
    initialData: [],
  });

  const { data: recipes } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => base44.entities.Recipe.list(),
    initialData: [],
  });

  const createSaleMutation = useMutation({
    mutationFn: (saleData) => base44.entities.Sale.create(saleData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      setIsDialogOpen(false);
      setEditingSale(null);
      resetForm();
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message);
    },
  });

  const updateSaleMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Sale.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      setIsDialogOpen(false);
      setEditingSale(null);
      resetForm();
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message);
    },
  });

  const deleteSaleMutation = useMutation({
    mutationFn: (id) => base44.entities.Sale.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      setDeleteSaleId(null);
    },
    onError: (error) => {
      alert(error?.response?.data?.detail || error.message || 'Delete failed');
      setDeleteSaleId(null);
    },
  });

  const resetForm = () => {
    setNewSale({
      date: format(new Date(), 'yyyy-MM-dd'),
      guests_count: '',
      total_amount: '',
      shift: 'Abend',
      items_sold: []
    });
  };

  const handleEdit = (sale) => {
    setEditingSale(sale);
    setNewSale({
      date: sale.date || format(new Date(), 'yyyy-MM-dd'),
      guests_count: sale.guests_count?.toString() || '',
      total_amount: sale.total_amount?.toString() || '',
      shift: sale.shift || 'Abend',
      items_sold: sale.items_sold || []
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const saleData = {
      ...newSale,
      guests_count: parseFloat(newSale.guests_count),
      total_amount: parseFloat(newSale.total_amount),
    };

    if (editingSale) {
      updateSaleMutation.mutate({ id: editingSale.id, data: saleData });
    } else {
      createSaleMutation.mutate(saleData);
    }
  };

  const addItem = () => {
    setNewSale({
      ...newSale,
      items_sold: [...newSale.items_sold, { recipe_name: '', quantity: 1 }]
    });
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...newSale.items_sold];
    updatedItems[index][field] = value;
    if (field === 'recipe_name') {
      const recipe = recipes.find(r => r.name === value);
      if (recipe) updatedItems[index].recipe_id = recipe.id;
    }
    setNewSale({ ...newSale, items_sold: updatedItems });
  };

  const removeItem = (index) => {
    setNewSale({
      ...newSale,
      items_sold: newSale.items_sold.filter((_, i) => i !== index)
    });
  };

  const toggleRow = (id) => setExpandedRow(expandedRow === id ? null : id);

  // Filter by source
  const filteredSales = sourceFilter === 'all'
    ? sales
    : sourceFilter === 'manual'
      ? sales.filter(s => !s.pos_provider)
      : sales.filter(s => s.pos_provider === sourceFilter);

  // Stats (on full dataset)
  const totalRevenue = sales.reduce((sum, s) => sum + (parseFloat(s.total_amount) || 0), 0);
  const totalGuests = sales.reduce((sum, s) => sum + (s.guests_count || 0), 0);
  const avgPerGuest = totalGuests > 0 ? totalRevenue / totalGuests : 0;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{t("sales.title")}</h1>
            <p className="text-slate-500 mt-1">{t("sales.subtitle")}</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) { setEditingSale(null); resetForm(); }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 shadow-md">
                <Plus className="w-4 h-4 mr-2" />
                {t("sales.addSale")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSale ? t("sales.editSale") : t("sales.newSale")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">{t("sales.date")}</Label>
                    <Input
                      id="date"
                      type="date"
                      value={newSale.date}
                      onChange={(e) => setNewSale({...newSale, date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="shift">{t("sales.shift")}</Label>
                    <Select value={newSale.shift} onValueChange={(value) => setNewSale({...newSale, shift: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mittag">{t("sales.lunch")}</SelectItem>
                        <SelectItem value="Abend">{t("sales.dinner")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="guests">{t("sales.guests")}</Label>
                    <Input
                      id="guests"
                      type="number"
                      min="0"
                      value={newSale.guests_count}
                      onChange={(e) => setNewSale({...newSale, guests_count: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="amount">{t("sales.revenue")}</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={newSale.total_amount}
                      onChange={(e) => setNewSale({...newSale, total_amount: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>{t("sales.itemsSold")}</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addItem}>
                      <Plus className="w-4 h-4 mr-1" />
                      {t("sales.addItem")}
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {newSale.items_sold.map((item, idx) => (
                      <div key={idx} className="flex gap-2 p-3 bg-slate-50 rounded-lg">
                        <Select
                          value={item.recipe_name}
                          onValueChange={(value) => updateItem(idx, 'recipe_name', value)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder={t("sales.selectItem")} />
                          </SelectTrigger>
                          <SelectContent>
                            {recipes.map(recipe => (
                              <SelectItem key={recipe.id} value={recipe.name}>
                                {recipe.name} (€{recipe.price?.toFixed(2)})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          min="1"
                          placeholder={t("sales.quantity")}
                          className="w-24"
                          value={item.quantity}
                          onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value))}
                        />
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {newSale.items_sold.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">
                        {t("sales.noItemsAdded")}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    {t("sales.cancel")}
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {editingSale ? t("sales.update") : t("sales.save")}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteSaleId} onOpenChange={() => setDeleteSaleId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("sales.deleteSale")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("sales.deleteSaleDesc")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("sales.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteSaleMutation.mutate(deleteSaleId)}
                className="bg-red-600 hover:bg-red-700"
              >
                {t("sales.delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-xl">
                  <Euro className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t("sales.totalRevenue")}</p>
                  <p className="text-2xl font-bold text-slate-900">€{totalRevenue.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t("sales.totalGuests")}</p>
                  <p className="text-2xl font-bold text-slate-900">{totalGuests}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t("sales.avgPerGuest")}</p>
                  <p className="text-2xl font-bold text-slate-900">€{avgPerGuest.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sales Table */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                {t("sales.salesHistory")}
              </CardTitle>
              {/* Source filter */}
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-500 whitespace-nowrap">{t("sales.source")}:</Label>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("sales.allSources")}</SelectItem>
                    <SelectItem value="manual">{t("sales.sourceManual")}</SelectItem>
                    <SelectItem value="sumup">SumUp</SelectItem>
                    <SelectItem value="lightspeed">Lightspeed</SelectItem>
                    <SelectItem value="orderbird">Orderbird</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-8"></TableHead>
                    <TableHead>{t("sales.date")}</TableHead>
                    <TableHead>{t("sales.shift")}</TableHead>
                    <TableHead>{t("sales.source")}</TableHead>
                    <TableHead>{t("sales.payment")}</TableHead>
                    <TableHead>{t("sales.table")}</TableHead>
                    <TableHead>{t("sales.guests")}</TableHead>
                    <TableHead>{t("sales.revenue")}</TableHead>
                    <TableHead>{t("sales.avgPerGuest")}</TableHead>
                    <TableHead className="text-right">{t("sales.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                        {t("sales.loading")}
                      </TableCell>
                    </TableRow>
                  ) : filteredSales.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                        {t("sales.noSales")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSales.map((sale) => {
                      const isExpanded = expandedRow === sale.id;
                      const hasLineItems = sale.line_items && sale.line_items.length > 0;
                      const posTime = sale.pos_timestamp
                        ? sale.pos_timestamp.substring(11, 16)
                        : null;

                      return (
                        <React.Fragment key={sale.id}>
                          <TableRow
                            className={`hover:bg-slate-50 ${hasLineItems ? 'cursor-pointer' : ''}`}
                            onClick={() => hasLineItems && toggleRow(sale.id)}
                          >
                            <TableCell className="pr-0">
                              {hasLineItems ? (
                                isExpanded
                                  ? <ChevronDown className="w-4 h-4 text-slate-400" />
                                  : <ChevronRight className="w-4 h-4 text-slate-400" />
                              ) : null}
                            </TableCell>
                            <TableCell className="font-medium">
                              <div>{format(new Date(sale.date), 'dd.MM.yyyy')}</div>
                              {posTime && (
                                <div className="text-xs text-slate-400">{posTime}</div>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                sale.shift === 'Mittag'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : sale.shift === 'Abend'
                                    ? 'bg-indigo-100 text-indigo-700'
                                    : 'bg-slate-100 text-slate-600'
                              }`}>
                                {sale.shift === 'Mittag'
                                  ? t("sales.lunch")
                                  : sale.shift === 'Abend'
                                    ? t("sales.dinner")
                                    : sale.shift}
                              </span>
                            </TableCell>
                            <TableCell>
                              <SourceBadge provider={sale.pos_provider} t={t} />
                            </TableCell>
                            <TableCell>
                              <PaymentBadge paymentType={sale.payment_type} t={t} />
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {sale.table_number || '—'}
                            </TableCell>
                            <TableCell>{sale.guests_count}</TableCell>
                            <TableCell className="font-semibold">
                              €{parseFloat(sale.total_amount).toFixed(2)}
                            </TableCell>
                            <TableCell className="text-slate-600">
                              €{sale.guests_count > 0
                                ? (parseFloat(sale.total_amount) / sale.guests_count).toFixed(2)
                                : '0.00'}
                            </TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  onClick={() => handleEdit(sale)}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => setDeleteSaleId(sale.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow className="bg-slate-50/70">
                              <TableCell colSpan={10} className="px-8 py-3">
                                <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                                  {t('sales.lineItems')}
                                </p>
                                <LineItemsRow sale={sale} t={t} />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
